// Widget 内部 Complete 内容组件 — 从 src/components/complete/contentNew.tsx 拷贝过来。
// 拷贝原因：原通用组件有项目锁死的字号/行高/颜色 SCSS，widget 主题套不进；这里独立
// 修改样式标记（data-peblla-part）和文案，让 widget-overrides.ts 一致命中，避免 CSS 打架。
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHideKeyboardOnScroll } from '@/hooks';
import { Form, Input } from '@/components';
import { Button, Checkbox } from 'antd';
import { profileApi } from '@/api';
import { commonStore } from '@/stores/quick-order';
import { storageHelper } from '@/utils';
import { useRequest } from '@/hooks';
import validSpecialCharacter from '@/utils/validSpecialCharacter';
import { changePhoneFormat } from '@/utils/changePhoneFormat';
import Cookies from 'js-cookie';
import type { StaticRenderModalConfig } from '@/components';
import type { FC } from 'react';
import { gtag_event_signup } from "@/utils/gTagEvent/gTagEvent";
import pointsTips from '@/components/pointsTips';
import { useText } from '../customization';

export type ContentProps = StaticRenderModalConfig & {
  sessionId: string;
  onOk?: () => void;
}

const Content: FC<ContentProps> = (props) => {
  const reqMemberInfoUpdate = useRequest({ method: 'put', url: profileApi.memberInfoUpdate });
  const form = Form.useForm();
  const [allowSms, setAllowSms] = useState(true);
  const scrollRef = useRef(null);
  useHideKeyboardOnScroll(scrollRef);

  const { memberInfo, updateState, showExpiredTip } = commonStore;
  const text = useText();

  useEffect(() => {
    if (memberInfo?.firstName || memberInfo?.lastName || memberInfo?.email) {
      if (memberInfo?.firstName) form.setFieldsValue({ firstName: memberInfo?.firstName });
      if (memberInfo?.lastName) form.setFieldsValue({ lastName: memberInfo?.lastName });
      if (memberInfo?.email) form.setFieldsValue({ email: memberInfo?.email });
    }
  }, [memberInfo]);

  const handleConfirm = async () => {
    if (!props.sessionId) {
      props?.onCancel?.();
      return;
    }
    try {
      const data = await form.validateFields();
      const { data: updateInfo } = await reqMemberInfoUpdate.send({
        ...data,
        id: memberInfo?.id,
        marketingNoticeStatus: allowSms ? 'CONSENTED' : 'NOT_CONSENTED'
      }, { headers: { sessionId: props.sessionId } });

      const path = window.__WEB_SITE_APP_CONFIG?.appConfig?.isPebllaDomain
        ? `/${window.__WEB_SITE_APP_CONFIG?.appConfig?.stylePath}` : '/';
      Cookies.set('__session_id', props.sessionId, { expires: 90, path: path });

      gtag_event_signup();

      const _memberInfo = { ...memberInfo, ...updateInfo };
      updateState({ memberInfo: _memberInfo });
      storageHelper.setItem('__member_info', _memberInfo);
      if (showExpiredTip) {
        pointsTips();
      }
      props?.onOk?.();
      props?.onCancel?.();
    } catch (ex) {
      console.warn(ex);
      return false;
    }
  };

  const handleClickTerms = () => window.open('https://www.peblla.com/terms-of-service');
  const handleClickPrivacy = () => window.open('https://www.peblla.com/privacy-policy');
  const phoneNumber = useMemo(() => changePhoneFormat(memberInfo?.phone), [memberInfo]);

  return (
    <div ref={scrollRef} style={{ height: '100%' }} data-peblla-part="body">
      <div data-peblla-part="complete-subtitle">
        {text.completeJoinWithText ?? 'Join with'} {phoneNumber}
      </div>
      <Form form={form} className='complete_form' data-peblla-part="complete-form">
        <div data-peblla-part="complete-name-row">
          <Form.Item
            requiredMark
            label={text.firstNameLabel ?? 'First Name'}
            name='firstName'
            labelProps={{ shrink: true }}
            rules={[
              { required: true },
              { pattern: /^[Α-￥a-zA-Z·&\\s]+$/, message: text.invalidNameMessage ?? 'Invalid name' },
              { pattern: (v: string) => validSpecialCharacter(v), message: text.illegalCharacterMessage ?? 'Illegal Character.' }
            ]}>
            <Input maxLength={50} placeholder={text.firstNamePlaceholder ?? 'First Name'} data-peblla-part="complete-input" />
          </Form.Item>
          <Form.Item
            requiredMark
            label={text.lastNameLabel ?? 'Last Name'}
            name='lastName'
            labelProps={{ shrink: true }}
            rules={[
              { required: true },
              { pattern: /^[Α-￥a-zA-Z·&\\s]+$/, message: text.invalidNameMessage ?? 'Invalid name' },
              { pattern: (v: string) => validSpecialCharacter(v), message: text.illegalCharacterMessage ?? 'Illegal Character.' }
            ]}>
            <Input maxLength={50} placeholder={text.lastNamePlaceholder ?? 'Last Name'} data-peblla-part="complete-input" />
          </Form.Item>
        </div>
        <Form.Item
          label={text.emailLabel ?? 'Email'}
          name='email'
          labelProps={{ shrink: true }}
          rules={[
            { pattern: /^(\w-*\.*)+@(\w-?)+(\.\w{2,})+$/, message: text.invalidEmailMessage ?? 'Please enter a valid email address.' }
          ]}>
          <Input placeholder={text.emailPlaceholder ?? 'Email'} data-peblla-part="complete-input" />
        </Form.Item>
        <div data-peblla-part="complete-allow-sms">
          <Form.Item label='' name='allowSms' labelProps={{ shrink: true }}>
            <Checkbox
              defaultChecked
              checked={allowSms}
              onChange={(e) => setAllowSms(e.target.checked)}
              data-peblla-part="complete-checkbox"
            >
              {text.allowSmsText ?? 'Get text messages with offers and rewards.'}
            </Checkbox>
          </Form.Item>
        </div>
        {/* 跟 loginContentNew 同结构：Form.Item 包按钮 + 同样 inline style，
            让 Continue 和 Sign Up 在两个抽屉里像素级一致 */}
        <Form.Item>
          <Button
            type='primary'
            style={{ width: '100%', height: 42 }}
            onClick={handleConfirm}
            loading={reqMemberInfoUpdate.loading}
            data-peblla-part="submit-btn"
          >
            {text.completeSubmitText ?? 'Sign Up'}
          </Button>
        </Form.Item>
      </Form>
      <div data-peblla-part="agreement">
        {text.agreementHTML ? (
          <span dangerouslySetInnerHTML={{ __html: text.agreementHTML }} />
        ) : (
          <span>
            By providing your phone number, you agree to join the rewards program and accept our{' '}
            <span onClick={handleClickTerms}>{"Platform's"} Terms</span>{' '}and{' '}
            <span onClick={handleClickPrivacy}>Privacy Policy</span>.
          </span>
        )}
      </div>
    </div>
  );
};

export default Content;
